/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { type SeoFaqsData, type SeoFaqsResponse } from '../interface';
import environmentConfig from '@/config/environment';
import { type Locale } from '@/types/i18n';
import { getCMSLocale } from '@/features/contentstack/utils/getCMSLocale';
import { useLocale } from '@/hooks/useLocale';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { usePathname } from 'next/navigation';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import { countryCodes } from '@/constants/locale';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/cs/v3/content_types/seo_faqs/entries`;

type Options = {
  url: string;
  locale: Locale;
};

const getSeoFaqs = async (options: Options, axiosInstance?: AxiosInstance) => {
  const { url: contentURL, locale } = options;

  const params = {
    environment: environmentConfig.CMS_ENV,
    branch: environmentConfig.CMS_BRANCH,
    locale: getCMSLocale(locale),
    query: JSON.stringify({
      url: contentURL,
    }),
  };

  const response = await getAxios(axiosInstance).get<SeoFaqsResponse>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

const selector = (data: SeoFaqsResponse): SeoFaqsData | null => data.entries[0] ?? null;

const { useData, prefetch } = createQuery<SeoFaqsData | null, Options>('seoFaqs', getSeoFaqs);

export const useSeoFaqs = () => {
  const locale = useLocale();
  const pathname = usePathname();
  const seoFaqsEnabled = useForteDecisionFlagWithTrueDefault('seoFaqsEnabled');
  const url = getPathnameFromAsPath(pathname);

  return useData({
    url,
    locale,
    enabled: Boolean(locale === countryCodes.enUS && url && seoFaqsEnabled),
  });
};

export const prefetchSeoFaqs = (
  options: Options,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetch(queryClient, options, axiosInstance);
};
